import {
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  styled,
  Typography,
} from '@mui/material';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { VehicleApplication } from '../types';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { SaveAlt } from '@mui/icons-material';
import useFeeTemplateOptions from 'hooks/lp/data/fee-manager/useFeeTemplateOptions';
import useEqualisationInterestFeeTemplateOptions from 'hooks/lp/data/vehicle/useEqualisationInterestFeeTemplateOptions';

const Card = styled(MuiCard)(({ theme }) => ({
  border: `1px solid ${theme.palette.success.main}`,
}));

const CardContent = styled(MuiCardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

const Document = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(theme.spacingSizes.sm),
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
  lineHeight: '16px',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
}));

const InputValue = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
  lineHeight: '16px',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
}));

type Props = {
  title: string;
  commitment: NonNullable<VehicleApplication['commitment']>;
  vehicle: Vehicle;
  investorLocalCurrency?: CurrencyCode;
};

const CommitmentPreview: React.FC<Props> = ({
  title,
  commitment,
  vehicle,
  investorLocalCurrency,
}) => {
  const carriedInterestFeeTemplate =
    vehicle.carrySettings.carriedInterestFeeTemplates.find(
      (template) => template._id === commitment.carriedInterestFeeTemplateId,
    )?.name ?? '--';

  const lpFeeTemplates = useFeeTemplateOptions(vehicle._id, false);
  const equalisationInterestFeeTemplates =
    useEqualisationInterestFeeTemplateOptions(vehicle._id);

  const lpFeeTemplate =
    lpFeeTemplates.find(
      (template) => template.value === commitment.lpFeeTemplateId,
    )?.label ?? '--';

  const equalisationInterestFeeTemplate =
    equalisationInterestFeeTemplates.find(
      (template) =>
        template.value === commitment.equalisationInterestFeeTemplateId,
    )?.label ?? '--';

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h5">{title}</Typography>
        <Row fullWidth spacing="sm">
          <FormField
            label="Subscription date"
            tooltip="This is the date the investor signed their fund documents and/or subscription agreement. This is not their close date, which is set through the Close & Equalise function."
          >
            <InputValue>
              {commitment.commitmentDate.toLabel('-', 'MMM dd, yyyy')}
            </InputValue>
          </FormField>
          <FormField label="Total commitment">
            <InputValue>
              {numberToCurrencyString(commitment.totalCommitmentLcy, {
                currency: vehicle.baseCurrency,
              })}
            </InputValue>
          </FormField>
          <FormField label="Investor local currency">
            <InputValue>{investorLocalCurrency ?? '--'}</InputValue>
          </FormField>
        </Row>
        <Row fullWidth spacing="sm">
          <FormField label="Equalisation interest fee template">
            <InputValue>{equalisationInterestFeeTemplate}</InputValue>
          </FormField>
          <FormField label="Fee template">
            <InputValue>{lpFeeTemplate}</InputValue>
          </FormField>
          <FormField label="Carried interest fee template">
            <InputValue>{carriedInterestFeeTemplate}</InputValue>
          </FormField>
        </Row>
        <MuiCard variant="outlined">
          <CardContent>
            <Column fullWidth spacing="sm">
              <Typography variant="h6">Subscription documents</Typography>
              {commitment.documents.map((document) => (
                <Document key={document.name}>
                  <Box>{document.name}</Box>
                  <a
                    href={document.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SaveAlt fontSize="small" />
                  </a>
                </Document>
              ))}
            </Column>
          </CardContent>
        </MuiCard>
      </CardContent>
    </Card>
  );
};

export default CommitmentPreview;
